import { Market } from '../components/ui/MarketSelector'
import { FindingGroup } from '../components/ui/FindingSection'
import { Verdict } from '../components/ui/VerdictBadge'
import { Confidence } from '../components/ui/ConfidenceTag'
import { RiskLevel } from '../components/ui/RiskIndicator'

export const markets: Market[] = [
  { code: 'AU', flag: '🇦🇺', name: 'Australia', authority: 'TGA' },
  { code: 'US', flag: '🇺🇸', name: 'United States', authority: 'FDA' },
  { code: 'EU', flag: '🇪🇺', name: 'European Union', authority: 'EC' },
  { code: 'UK', flag: '🇬🇧', name: 'United Kingdom', authority: 'MHRA' },
  { code: 'CA', flag: '🇨🇦', name: 'Canada', authority: 'Health Canada' },
]

export interface MarketReadiness {
  code: string
  flag: string
  name: string
  authority: string
  score: number
  pathwayRisk: RiskLevel
  pathwayLabel: string
  status: string
}

export interface ExtractedField {
  label: string
  value: string
  confidence: Confidence
}

export interface Ingredient {
  name: string
  inci: string
  function: string
  concentration: string
  verdict: Verdict
  limit?: string
  confidence: Confidence
}

export interface Dossier {
  id: string
  name: string
  brand: string
  category: string
  updated: string
  primaryMarket: string
  readiness: number
  ingredientRisk: RiskLevel
  ingredientRiskLabel: string
  openIssues: number
  blocking: number
  highestRiskMarket: string
  classification: string
  pathwayRisk: RiskLevel
  pathwayLabel: string
  summary: string
  marketReadiness: MarketReadiness[]
  extractedFields: ExtractedField[]
  ingredients: Ingredient[]
  formulaSummary: string
  labelFindings: FindingGroup[]
  claimFindings: FindingGroup[]
  documents: DossierDocument[]
}

export interface DossierDocument {
  name: string
  type: string
  pages: number
  uploaded: string
  status: 'Verified' | 'Processing' | 'Needs review'
}

export const dossiers: Dossier[] = [
  {
    id: 'lumefit-serum',
    name: 'LumaFit Radiance Serum',
    brand: 'LumaFit',
    category: 'Skincare · Serum',
    updated: '2 hours ago',
    primaryMarket: 'AU',
    readiness: 68,
    ingredientRisk: 'medium',
    ingredientRiskLabel: 'Medium risk',
    openIssues: 7,
    blocking: 1,
    highestRiskMarket: 'AU',
    classification: 'AUST L (Listed)',
    pathwayRisk: 'low',
    pathwayLabel: 'Low risk',
    summary:
      'This serum is largely aligned with the AUST L listed-medicine pathway. One blocking label issue and a restricted ingredient concentration need action before submission. Comparative read: Australia is currently the furthest from ready due to the label warning gap.',
    marketReadiness: [
      { code: 'AU', flag: '🇦🇺', name: 'Australia', authority: 'TGA', score: 68, pathwayRisk: 'low', pathwayLabel: 'Low risk', status: '1 blocking issue' },
      { code: 'US', flag: '🇺🇸', name: 'United States', authority: 'FDA', score: 82, pathwayRisk: 'low', pathwayLabel: 'Low risk', status: 'On track' },
      { code: 'EU', flag: '🇪🇺', name: 'European Union', authority: 'EC', score: 74, pathwayRisk: 'medium', pathwayLabel: 'Medium risk', status: '2 open issues' },
      { code: 'UK', flag: '🇬🇧', name: 'United Kingdom', authority: 'MHRA', score: 79, pathwayRisk: 'low', pathwayLabel: 'Low risk', status: 'On track' },
      { code: 'CA', flag: '🇨🇦', name: 'Canada', authority: 'Health Canada', score: 71, pathwayRisk: 'medium', pathwayLabel: 'Medium risk', status: '3 open issues' },
    ],
    extractedFields: [
      { label: 'Product name', value: 'LumaFit Radiance Serum', confidence: 'HIGH' },
      { label: 'Net content', value: '30 mL', confidence: 'HIGH' },
      { label: 'Batch / Lot', value: 'LF-2409-A', confidence: 'MEDIUM' },
      { label: 'Expiry (PAO)', value: '12M after opening', confidence: 'HIGH' },
      { label: 'Responsible sponsor', value: 'LumaFit Labs Pty Ltd', confidence: 'MEDIUM' },
      { label: 'Country of origin', value: 'Australia', confidence: 'LOW' },
    ],
    formulaSummary:
      'The formulation is dominated by well-characterised, permitted actives. Ingredient risk is driven by the retinol concentration, which sits close to the listed-pathway ceiling and requires a mandatory advisory statement.',
    ingredients: [
      { name: 'Aqua', inci: 'Water', function: 'Solvent', concentration: 'to 100%', verdict: 'Permitted', confidence: 'HIGH' },
      { name: 'Niacinamide', inci: 'Niacinamide', function: 'Active', concentration: '5.0%', verdict: 'Permitted with conditions', limit: '≤ 10%', confidence: 'HIGH' },
      { name: 'Retinol', inci: 'Retinol', function: 'Active', concentration: '1.0%', verdict: 'Permitted with conditions', limit: '≤ 1.0% (advisory required)', confidence: 'HIGH' },
      { name: 'Hyaluronic Acid', inci: 'Sodium Hyaluronate', function: 'Humectant', concentration: '2.0%', verdict: 'Permitted', confidence: 'HIGH' },
      { name: 'Phenoxyethanol', inci: 'Phenoxyethanol', function: 'Preservative', concentration: '0.9%', verdict: 'Permitted with conditions', limit: '≤ 1.0%', confidence: 'HIGH' },
      { name: 'Fragrance', inci: 'Parfum', function: 'Fragrance', concentration: '0.3%', verdict: 'Verification required', confidence: 'LOW' },
    ],
    labelFindings: [
      {
        id: 'mandatory',
        title: 'Mandatory statements',
        findings: [
          {
            id: 'l1',
            name: 'Retinol advisory statement present',
            status: 'Fail',
            severity: 'Blocking',
            detail:
              'Products containing retinol at 1.0% must carry the advisory statement "Not to be used by pregnant or breastfeeding women" on the label. This statement was not detected on the supplied artwork.',
            recommendation: 'Add the mandatory pregnancy/breastfeeding advisory to the back-of-pack panel.',
            citations: [
              { document: 'TGA Listed Medicines Labelling Order', page: 14, extract: 'Where the medicine contains retinol, the label must include the advisory statement regarding use during pregnancy or breastfeeding.' },
            ],
          },
          {
            id: 'l2',
            name: 'Net content declared',
            status: 'Pass',
            detail: 'Net content "30 mL" is present on the front panel in the required format.',
            citations: [{ document: 'Label artwork v3.pdf', page: 1, extract: 'Net content: 30 mL e' }],
          },
          {
            id: 'l3',
            name: 'Sponsor name and address',
            status: 'Pass',
            detail: 'Australian sponsor name and address are present on the back panel.',
          },
        ],
      },
      {
        id: 'directions',
        title: 'Directions & warnings',
        findings: [
          {
            id: 'l4',
            name: 'Directions for use present',
            status: 'Pass',
            detail: 'Clear directions for use are present.',
          },
          {
            id: 'l5',
            name: 'Storage conditions declared',
            status: 'Fail',
            severity: 'Medium',
            detail: 'No storage condition statement was detected. Listed medicines should declare storage conditions where stability depends on them.',
            recommendation: 'Add "Store below 25°C" to the label.',
          },
        ],
      },
    ],
    claimFindings: [
      {
        id: 'efficacy',
        title: 'Efficacy claims',
        findings: [
          {
            id: 'c1',
            name: '"Reduces the appearance of fine lines"',
            status: 'Pass',
            detail: 'This is an acceptable cosmetic appearance claim under the listed pathway.',
          },
          {
            id: 'c2',
            name: '"Clinically proven to reverse ageing"',
            status: 'Fail',
            severity: 'High',
            detail: 'A claim to "reverse ageing" implies a therapeutic effect beyond the listed indications permitted for this pathway, and "clinically proven" requires held evidence.',
            recommendation: 'Reword to an appearance-based claim and ensure supporting evidence is on file.',
            citations: [{ document: 'AU Therapeutic Goods Advertising Code', page: 22, extract: 'Claims must not overstate the effect of the goods or refer to prohibited representations.' }],
          },
        ],
      },
      {
        id: 'safety',
        title: 'Safety & comparative claims',
        findings: [
          {
            id: 'c3',
            name: '"Suitable for sensitive skin"',
            status: 'Fail',
            severity: 'To action',
            detail: 'A "suitable for sensitive skin" claim should be backed by an appropriate tolerance/patch-test dataset.',
            recommendation: 'Attach the tolerance study to the dossier or soften the claim.',
          },
          { id: 'c4', name: '"Fragrance contains allergens (declared)"', status: 'Pass', detail: 'Allergen declaration is consistent with the fragrance breakdown provided.' },
        ],
      },
    ],
    documents: [
      { name: 'Label artwork v3.pdf', type: 'Artwork', pages: 2, uploaded: '2 hours ago', status: 'Verified' },
      { name: 'Full formulation breakdown.xlsx', type: 'Formula', pages: 1, uploaded: '1 day ago', status: 'Verified' },
      { name: 'Safety assessment (CPSR).pdf', type: 'Safety', pages: 34, uploaded: '3 days ago', status: 'Verified' },
      { name: 'Tolerance study draft.docx', type: 'Evidence', pages: 12, uploaded: '5 hours ago', status: 'Needs review' },
      { name: 'Stability data.pdf', type: 'Stability', pages: 8, uploaded: '2 days ago', status: 'Processing' },
    ],
  },
  {
    id: 'vitaboost-gummies',
    name: 'VitaBoost Immunity Gummies',
    brand: 'VitaBoost',
    category: 'Supplement · Gummy',
    updated: 'Yesterday',
    primaryMarket: 'AU',
    readiness: 84,
    ingredientRisk: 'low',
    ingredientRiskLabel: 'Low risk',
    openIssues: 3,
    blocking: 0,
    highestRiskMarket: 'EU',
    classification: 'AUST L (Listed)',
    pathwayRisk: 'low',
    pathwayLabel: 'Low risk',
    summary:
      'A well-documented listed supplement. All ingredients sit within permitted limits; remaining issues are minor label refinements. Comparative read: the EU is furthest from ready due to a novel-food question on one botanical.',
    marketReadiness: [
      { code: 'AU', flag: '🇦🇺', name: 'Australia', authority: 'TGA', score: 84, pathwayRisk: 'low', pathwayLabel: 'Low risk', status: 'On track' },
      { code: 'US', flag: '🇺🇸', name: 'United States', authority: 'FDA', score: 88, pathwayRisk: 'low', pathwayLabel: 'Low risk', status: 'On track' },
      { code: 'EU', flag: '🇪🇺', name: 'European Union', authority: 'EC', score: 61, pathwayRisk: 'high', pathwayLabel: 'High risk', status: 'Novel food query' },
      { code: 'UK', flag: '🇬🇧', name: 'United Kingdom', authority: 'MHRA', score: 80, pathwayRisk: 'low', pathwayLabel: 'Low risk', status: '1 open issue' },
      { code: 'CA', flag: '🇨🇦', name: 'Canada', authority: 'Health Canada', score: 83, pathwayRisk: 'low', pathwayLabel: 'Low risk', status: 'On track' },
    ],
    extractedFields: [
      { label: 'Product name', value: 'VitaBoost Immunity Gummies', confidence: 'HIGH' },
      { label: 'Net content', value: '60 gummies', confidence: 'HIGH' },
      { label: 'Serving size', value: '2 gummies', confidence: 'HIGH' },
      { label: 'Batch / Lot', value: 'VB-2408-C', confidence: 'MEDIUM' },
      { label: 'Responsible sponsor', value: 'VitaBoost Nutrition Pty Ltd', confidence: 'HIGH' },
      { label: 'Country of origin', value: 'Australia', confidence: 'MEDIUM' },
    ],
    formulaSummary:
      'All actives are on the permitted ingredient list within listed limits. Ingredient risk is low; the only watch item is an elderberry extract flagged as a potential novel food in the EU.',
    ingredients: [
      { name: 'Vitamin C', inci: 'Ascorbic Acid', function: 'Active', concentration: '80 mg', verdict: 'Permitted with conditions', limit: '≤ 1000 mg', confidence: 'HIGH' },
      { name: 'Zinc', inci: 'Zinc Citrate', function: 'Active', concentration: '10 mg', verdict: 'Permitted with conditions', limit: '≤ 25 mg', confidence: 'HIGH' },
      { name: 'Elderberry', inci: 'Sambucus Nigra Extract', function: 'Active', concentration: '150 mg', verdict: 'Permitted with conditions', limit: 'Novel food query (EU)', confidence: 'MEDIUM' },
      { name: 'Pectin', inci: 'Pectin', function: 'Gelling agent', concentration: 'q.s.', verdict: 'Permitted', confidence: 'HIGH' },
      { name: 'Glucose syrup', inci: 'Glucose Syrup', function: 'Base', concentration: 'q.s.', verdict: 'Permitted', confidence: 'HIGH' },
    ],
    labelFindings: [
      {
        id: 'mandatory',
        title: 'Mandatory statements',
        findings: [
          { id: 'v1', name: 'Recommended daily dose present', status: 'Pass', detail: 'Recommended dose "2 gummies daily" is present.' },
          { id: 'v2', name: 'Allergen declaration', status: 'Fail', severity: 'Medium', detail: 'Contains-glucose statement present but "may contain traces" cross-contamination line is missing.', recommendation: 'Add a precautionary allergen statement.' },
          { id: 'v3', name: 'Warning: keep out of reach of children', status: 'Pass', detail: 'Child safety warning present.' },
        ],
      },
    ],
    claimFindings: [
      {
        id: 'efficacy',
        title: 'Efficacy claims',
        findings: [
          { id: 'vc1', name: '"Supports immune function"', status: 'Pass', detail: 'Permitted indication for listed medicines with the vitamin C content declared.' },
          { id: 'vc2', name: '"Prevents colds and flu"', status: 'Fail', severity: 'High', detail: 'A prevention-of-disease claim is not permitted for the listed pathway.', recommendation: 'Reword to an immune-support indication.' },
        ],
      },
    ],
    documents: [
      { name: 'Gummy label v2.pdf', type: 'Artwork', pages: 1, uploaded: 'Yesterday', status: 'Verified' },
      { name: 'Formulation sheet.xlsx', type: 'Formula', pages: 1, uploaded: '2 days ago', status: 'Verified' },
      { name: 'Elderberry novel-food dossier.pdf', type: 'Evidence', pages: 22, uploaded: '4 days ago', status: 'Needs review' },
    ],
  },
  {
    id: 'purecalm-balm',
    name: 'PureCalm Recovery Balm',
    brand: 'PureCalm',
    category: 'Skincare · Balm',
    updated: '3 days ago',
    primaryMarket: 'AU',
    readiness: 45,
    ingredientRisk: 'high',
    ingredientRiskLabel: 'High risk',
    openIssues: 11,
    blocking: 2,
    highestRiskMarket: 'AU',
    classification: 'Assessment required',
    pathwayRisk: 'high',
    pathwayLabel: 'High risk',
    summary:
      'This balm makes therapeutic recovery claims and contains an ingredient that may push it out of the listed pathway. Two blocking issues remain. Comparative read: Australia is furthest from ready — the pathway itself is unresolved.',
    marketReadiness: [
      { code: 'AU', flag: '🇦🇺', name: 'Australia', authority: 'TGA', score: 45, pathwayRisk: 'high', pathwayLabel: 'High risk', status: '2 blocking issues' },
      { code: 'US', flag: '🇺🇸', name: 'United States', authority: 'FDA', score: 63, pathwayRisk: 'medium', pathwayLabel: 'Medium risk', status: '4 open issues' },
      { code: 'EU', flag: '🇪🇺', name: 'European Union', authority: 'EC', score: 58, pathwayRisk: 'medium', pathwayLabel: 'Medium risk', status: '3 open issues' },
      { code: 'UK', flag: '🇬🇧', name: 'United Kingdom', authority: 'MHRA', score: 60, pathwayRisk: 'medium', pathwayLabel: 'Medium risk', status: '3 open issues' },
      { code: 'CA', flag: '🇨🇦', name: 'Canada', authority: 'Health Canada', score: 55, pathwayRisk: 'medium', pathwayLabel: 'Medium risk', status: '4 open issues' },
    ],
    extractedFields: [
      { label: 'Product name', value: 'PureCalm Recovery Balm', confidence: 'HIGH' },
      { label: 'Net content', value: '50 g', confidence: 'HIGH' },
      { label: 'Batch / Lot', value: 'PC-2407-B', confidence: 'LOW' },
      { label: 'Responsible sponsor', value: 'PureCalm Co', confidence: 'LOW' },
      { label: 'Country of origin', value: 'Not detected', confidence: 'LOW' },
    ],
    formulaSummary:
      'Ingredient risk is high: the menthol/camphor combination at the declared concentrations, together with the therapeutic positioning, may require a registered rather than listed pathway.',
    ingredients: [
      { name: 'Camphor', inci: 'Camphor', function: 'Active', concentration: '4.0%', verdict: 'Permitted with conditions', limit: '≤ 3% (topical)', confidence: 'HIGH' },
      { name: 'Menthol', inci: 'Menthol', function: 'Active', concentration: '5.0%', verdict: 'Permitted with conditions', limit: '≤ 5%', confidence: 'HIGH' },
      { name: 'Arnica', inci: 'Arnica Montana Extract', function: 'Active', concentration: '2.0%', verdict: 'Inconclusive', confidence: 'LOW' },
      { name: 'Beeswax', inci: 'Cera Alba', function: 'Base', concentration: 'q.s.', verdict: 'Permitted', confidence: 'HIGH' },
      { name: 'Fragrance', inci: 'Parfum', function: 'Fragrance', concentration: '0.5%', verdict: 'Verification required', confidence: 'LOW' },
    ],
    labelFindings: [
      {
        id: 'mandatory',
        title: 'Mandatory statements',
        findings: [
          { id: 'p1', name: 'Camphor concentration warning', status: 'Fail', severity: 'Blocking', detail: 'Camphor at 4.0% exceeds the 3% topical ceiling and needs a concentration warning or reformulation.', recommendation: 'Reduce camphor to ≤ 3% or move to a registered pathway.' },
          { id: 'p2', name: 'Sponsor address complete', status: 'Fail', severity: 'Blocking', detail: 'No Australian sponsor address detected on artwork.', recommendation: 'Add the full sponsor address.' },
          { id: 'p3', name: 'Net content declared', status: 'Pass', detail: 'Net content "50 g" present.' },
        ],
      },
    ],
    claimFindings: [
      {
        id: 'efficacy',
        title: 'Therapeutic claims',
        findings: [
          { id: 'pc1', name: '"Relieves muscle and joint pain"', status: 'Fail', severity: 'High', detail: 'A pain-relief claim is therapeutic and may require a registered pathway.', recommendation: 'Reassess pathway or reword.' },
          { id: 'pc2', name: '"Cooling sensation"', status: 'Pass', detail: 'Sensory claim is acceptable.' },
        ],
      },
    ],
    documents: [
      { name: 'Balm artwork v1.pdf', type: 'Artwork', pages: 1, uploaded: '3 days ago', status: 'Needs review' },
      { name: 'Formula draft.xlsx', type: 'Formula', pages: 1, uploaded: '3 days ago', status: 'Processing' },
    ],
  },
]

export function getDossier(id: string): Dossier | undefined {
  return dossiers.find((d) => d.id === id)
}
